import React, { useEffect, useState } from 'react';
import { Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { oapi, splitList, uploadOfficialPhoto } from '@/lib/officialsApi';
import { OfficialAvatar } from './StatusPill';

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', photo_path: '', sports: '', certifications: '', positions: [], notes: '', status: 'active' };

const toForm = (o) => ({
  ...EMPTY, first_name: o.first_name || '', last_name: o.last_name || '', email: o.email || '', phone: o.phone || '',
  photo_path: o.photo_path || '', sports: (o.sports || []).join(', '), certifications: (o.certifications || []).join(', '),
  positions: o.positions || [], notes: o.notes || '', status: o.status || 'active',
});

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;

const PhotoPicker = ({ name, photo, uploading, onFile }) => (
  <div className="flex items-center gap-3">
    <OfficialAvatar name={name || '?'} photo={photo} size="w-14 h-14" />
    <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00247D] cursor-pointer hover:underline" data-testid="official-photo-upload">
      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}{photo ? 'Change photo' : 'Add photo'}
      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="official-photo-input" />
    </label>
  </div>
);

export const OfficialFormDialog = ({ open, orgId, official, roles, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (open) setF(official ? toForm(official) : EMPTY); }, [open, official]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const togglePos = (k) => set('positions', f.positions.includes(k) ? f.positions.filter(x => x !== k) : [...f.positions, k]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try { set('photo_path', await uploadOfficialPhoto(file)); } catch (er) { toast.error(er.message); } finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() && !f.last_name.trim()) { toast.error('Add a first or last name'); return; }
    setBusy(true);
    const body = { ...f, sports: splitList(f.sports), certifications: splitList(f.certifications) };
    try {
      const r = official ? await oapi(`/officials/list/${official.id}`, { method: 'PUT', orgId, body })
        : await oapi('/officials/list', { method: 'POST', orgId, body });
      toast.success(official ? 'Official updated' : `${r.official.full_name} added`);
      onSaved(r.official);
    } catch (er) { toast.error(er.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg p-0 gap-0" data-testid="official-form-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-[#00247D]">{official ? `Edit ${official.full_name}` : 'Add official'}</DialogTitle>
          <DialogDescription>Contact details are only visible to you and your schedulers.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
            <PhotoPicker name={`${f.first_name} ${f.last_name}`} photo={f.photo_path} uploading={uploading} onFile={onFile} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name"><Input value={f.first_name} onChange={e => set('first_name', e.target.value)} data-testid="official-first-name" /></Field>
              <Field label="Last name"><Input value={f.last_name} onChange={e => set('last_name', e.target.value)} data-testid="official-last-name" /></Field>
              <Field label="Email"><Input type="email" value={f.email} onChange={e => set('email', e.target.value)} data-testid="official-email" /></Field>
              <Field label="Phone"><Input value={f.phone} onChange={e => set('phone', e.target.value)} data-testid="official-phone" /></Field>
            </div>
            <Field label="Sports (comma separated)"><Input value={f.sports} onChange={e => set('sports', e.target.value)} placeholder="basketball, soccer" data-testid="official-sports" /></Field>
            <Field label="Certifications / qualifications"><Input value={f.certifications} onChange={e => set('certifications', e.target.value)} placeholder="FIBA Level 2" data-testid="official-certifications" /></Field>
            <Field label="Preferred positions">
              <div className="flex flex-wrap gap-1.5" data-testid="official-positions">
                {roles.map(r => (
                  <button key={r.key} type="button" onClick={() => togglePos(r.key)} data-testid={`official-position-${r.key}`}
                    className={`px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${f.positions.includes(r.key) ? 'bg-[#00247D] border-[#00247D] text-white' : 'bg-white text-slate-600 hover:border-[#00247D]/50'}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Private notes"><Textarea rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} data-testid="official-notes" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={f.status === 'active'} onCheckedChange={v => set('status', v ? 'active' : 'inactive')} data-testid="official-status-switch" />
              Active (can be assigned)
            </label>
          </div>
          <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50 rounded-b-lg">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || uploading} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="official-form-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{official ? 'Save changes' : 'Add official'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
